import type { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid';
import type { GridApiPro } from '@mui/x-data-grid-pro';

type ApiRefLike = { current: GridApiPro | null } | undefined;

/**
 * Resolve a MUI X v9 row-selection model to the concrete list of selected
 * row ids.
 *
 * v9 replaced the old `GridRowId[]` selection model with a tagged object,
 * `{ type: 'include' | 'exclude'; ids: Set<GridRowId> }`:
 *  - `include` → `ids` ARE the selected rows.
 *  - `exclude` → every row EXCEPT `ids` is selected. The header "select all"
 *    checkbox emits `exclude` with an empty `ids` set, so reading `ids`
 *    directly returns nothing — which is why select-all silently selected
 *    no rows after the v9 upgrade. For the exclude case we subtract the
 *    excluded ids from every row currently loaded in the grid
 *    (`getAllRowIds`).
 */
export function getSelectedRowIds(model: GridRowSelectionModel, apiRef: ApiRefLike): GridRowId[] {
  if (model.type === 'include') {
    return Array.from(model.ids);
  }

  const allRowIds = apiRef?.current?.getAllRowIds() ?? [];
  return allRowIds.filter((id) => !model.ids.has(id));
}
